import { FiCode, FiLayers, FiZap, FiHeart } from "react-icons/fi";
import Reveal from "../ui/Reveal";
import SectionHeading from "../ui/SectionHeading";

const principles = [
  {
    Icon: FiLayers,
    title: "Full-stack, end to end",
    text: "From database schema to the last hover state — I like owning the whole path a feature travels.",
  },
  {
    Icon: FiCode,
    title: "Code that stays readable",
    text: "Small components, honest names, no cleverness for its own sake. Future-me has to maintain it too.",
  },
  {
    Icon: FiZap,
    title: "Fast by default",
    text: "Performance is a feature. Lean queries, sensible bundles, interfaces that respond immediately.",
  },
  {
    Icon: FiHeart,
    title: "Detail as a habit",
    text: "Spacing, timing, empty states. The small things are what make a product feel finished.",
  },
];

const facts = [
  { label: "Based in", value: "Kosovo · Remote friendly" },
  { label: "Currently", value: "MSc Computer Science" },
  { label: "Experience", value: "Skymirror Inc. — 6 months" },
  { label: "Focus", value: "React · Laravel · Dashboards" },
];

const About = () => (
  <section id="about" className="section bg-cream">
    <div className="pointer-events-none absolute -left-40 top-24 h-[420px] w-[420px] rounded-full glow-frost" />

    <div className="shell relative">
      <SectionHeading
        index="01"
        eyebrow="About"
        title="I build digital products with clarity,"
        accent="purpose and care."
        tone="light"
      />

      <div className="mt-16 grid gap-14 lg:grid-cols-[1fr_0.95fr] lg:gap-20">
        {/* Narrative */}
        <div>
          <Reveal>
            <p className="font-display text-2xl leading-[1.5] text-ink/85 sm:text-[1.7rem]">
              I’m a full-stack developer who cares as much about how a product
              feels as about how it’s wired underneath.
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <p className="mt-7 text-[1.02rem] leading-8 text-stone">
              My work lives around React, Laravel, Tailwind and MySQL. I build
              interfaces that stay calm under real content, and backends that
              stay predictable as a project grows — dashboards, platforms,
              admin panels and the unglamorous plumbing that holds them up.
            </p>
          </Reveal>

          <Reveal delay={0.14}>
            <p className="mt-5 text-[1.02rem] leading-8 text-stone">
              During six months of professional practice at{" "}
              <span className="font-semibold text-ink">Skymirror Inc.</span> I
              worked on real software with real deadlines — which taught me more
              about architecture, review and shipping than any tutorial could.
            </p>
          </Reveal>

          {/* Fact table */}
          <div className="mt-12 divide-y divide-ink/10 border-y border-ink/10">
            {facts.map((fact, index) => (
              <Reveal key={fact.label} delay={0.05 * index}>
                <div className="flex flex-wrap items-baseline justify-between gap-3 py-4">
                  <span className="eyebrow text-moss/70">{fact.label}</span>
                  <span className="text-[15px] font-medium text-ink">
                    {fact.value}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Principles */}
        <div className="grid gap-4 sm:grid-cols-2">
          {principles.map((item, index) => (
            <Reveal key={item.title} delay={0.06 * index} from="up">
              <article className="group h-full rounded-3xl border border-ink/[0.07] bg-white/70 p-7 transition duration-500 hover:-translate-y-1.5 hover:border-moss/25 hover:bg-white hover:shadow-lift">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-frost text-lg text-pine transition-colors duration-500 group-hover:bg-pine group-hover:text-mint">
                  <item.Icon />
                </span>

                <h3 className="mt-6 font-display text-xl leading-snug text-ink">
                  {item.title}
                </h3>

                <p className="mt-3 text-[14.5px] leading-7 text-stone">
                  {item.text}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default About;
